<template>
    <!-- Edit Document Modal -->
    <a-popup :visible="visible" @cancel="onCancel">
        <div class="tw-mx-auto tw-w-[95%] md:tw-max-w-[600px] tw-bg-white tw-rounded-2xl tw-shadow-lg tw-overflow-hidden">
            <!-- Header -->
            <div class="tw-p-6 tw-border-b tw-border-gray-200">
                <h2 class="tw-text-xl tw-font-semibold tw-text-gray-900">Edit Document</h2>
                <p class="tw-text-sm tw-text-gray-600 tw-mt-1">Update document details and files</p>
            </div>

            <!-- Content -->
            <div class="tw-p-6">
                <!-- Current Document Info -->
                <div v-if="document" class="tw-mb-6">
                    <h3 class="tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-3">Current Document</h3>
                    <div class="tw-flex tw-items-center tw-gap-4 tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                        <!-- Current Thumbnail -->
                        <div class="tw-w-16 tw-h-16 tw-flex-shrink-0">
                            <img
                                v-if="document.thumbnail_url"
                                :src="document.thumbnail_url"
                                :alt="document.name"
                                class="tw-w-full tw-h-full tw-object-cover tw-rounded"
                            />
                            <div v-else class="tw-w-full tw-h-full tw-bg-gray-200 tw-rounded tw-flex tw-items-center tw-justify-center">
                                <icon :icon="getFileIcon(document.type)" class="tw-text-2xl tw-text-gray-400" />
                            </div>
                        </div>
                        
                        <!-- Current Info -->
                        <div class="tw-flex-1 tw-min-w-0">
                            <h4 class="tw-font-medium tw-text-gray-900 tw-truncate">{{ document.name }}</h4>
                            <p class="tw-text-sm tw-text-gray-500">{{ formatFileSize(document.size) }} • {{ document.type.toUpperCase() }}</p>
                            <p class="tw-text-xs tw-text-gray-400">Created {{ formatDate(document.created_at) }}</p>
                        </div>
                    </div>
                </div>

                <!-- Edit Form -->
                <div class="tw-space-y-4">
                    <!-- Basic Info -->
                    <FormField
                        v-model="editForm.name"
                        type="text"
                        label="Name"
                        placeholder="Document name (max 255 characters)"
                        left-icon="solar:document-text-linear"
                        hide-errors
                        required
                    />
                    
                    <FormField
                        v-model="editForm.description"
                        type="textarea"
                        label="Description"
                        placeholder="Document description (max 255 characters)"
                        hide-errors
                        rows="3"
                        required
                    />
                    
                    <!-- Status Toggle -->
                    <div class="tw-space-y-2">
                        <label class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-gray-700">
                            <icon icon="solar:settings-linear" class="tw-text-base" />
                            Status
                        </label>
                        <div class="tw-flex tw-items-center tw-gap-3 tw-p-2 tw-border tw-border-gray-200 tw-rounded-lg tw-bg-white">
                            <!-- Active Button -->
                            <button
                                type="button"
                                @click="editForm.status = 'active'"
                                :class="[
                                    'tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-transition-all tw-duration-200 tw-flex-1 tw-justify-center',
                                    editForm.status === 'active' 
                                        ? 'tw-bg-green-500 tw-text-white tw-shadow-sm' 
                                        : 'tw-text-gray-600 hover:tw-bg-gray-50'
                                ]"
                            >
                                <icon 
                                    :icon="editForm.status === 'active' ? 'solar:check-circle-bold' : 'solar:check-circle-linear'" 
                                    class="tw-text-base" 
                                />
                                Active
                            </button>
                            
                            <!-- Inactive Button -->
                            <button
                                type="button"
                                @click="editForm.status = 'inactive'"
                                :class="[
                                    'tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-transition-all tw-duration-200 tw-flex-1 tw-justify-center',
                                    editForm.status === 'inactive' 
                                        ? 'tw-bg-red-500 tw-text-white tw-shadow-sm' 
                                        : 'tw-text-gray-600 hover:tw-bg-gray-50'
                                ]"
                            >
                                <icon 
                                    :icon="editForm.status === 'inactive' ? 'solar:close-circle-bold' : 'solar:close-circle-linear'" 
                                    class="tw-text-base" 
                                />
                                Inactive
                            </button>
                        </div>
                    </div>
                    
                    <!-- Replace Document File (Optional) -->
                    <div class="tw-space-y-2">
                        <label class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-gray-700">
                            <icon icon="solar:document-linear" class="tw-text-base" />
                            Replace Document File 
                            <span class="tw-text-xs tw-text-gray-500 tw-font-normal">(Optional)</span>
                        </label>
                        
                        <!-- File Upload Area -->
                        <div v-if="!newFile">
                            <div
                                @drop.prevent="handleFileDrop"
                                @dragover.prevent="isDraggingFile = true"
                                @dragleave.prevent="isDraggingFile = false"
                                @click="triggerFileInput"
                                :class="[
                                    'tw-relative tw-w-full tw-h-20 tw-border-2 tw-border-dashed tw-rounded-lg tw-flex tw-flex-col tw-items-center tw-justify-center tw-cursor-pointer tw-transition-all tw-duration-200',
                                    isDraggingFile ? 'tw-border-primary-500 tw-bg-primary-50' : 'tw-border-gray-300 hover:tw-border-primary-400'
                                ]"
                            >
                                <icon icon="solar:document-add-linear" class="tw-text-xl tw-text-gray-400" />
                                <p class="tw-text-xs tw-text-gray-500 tw-text-center tw-px-2">
                                    Drop new file or click to replace<br />
                                    PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX
                                </p>
                                
                                <input
                                    ref="fileInput"
                                    type="file"
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                                    @change="handleFileSelect"
                                    class="tw-hidden"
                                />
                            </div>
                        </div>
                        
                        <!-- New File Preview -->
                        <div v-else class="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg">
                            <icon :icon="getFileIcon(newFileType)" class="tw-text-2xl tw-text-green-600 tw-flex-shrink-0" />
                            <div class="tw-flex-1 tw-min-w-0">
                                <p class="tw-text-sm tw-font-medium tw-text-green-900 tw-truncate">{{ newFile.name }}</p>
                                <p class="tw-text-xs tw-text-green-600">{{ formatFileSize(newFile.size) }} • Will replace current file</p>
                            </div>
                            <button
                                @click="removeNewFile"
                                class="tw-text-green-600 hover:tw-text-red-500 tw-flex-shrink-0"
                            >
                                <icon icon="solar:close-circle-bold" class="tw-text-lg" />
                            </button>
                        </div>
                    </div>
                    
                    <!-- Replace Thumbnail (Optional) -->
                    <div class="tw-space-y-2">
                        <label class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-gray-700">
                            <icon icon="solar:image-linear" class="tw-text-base" />
                            Replace Thumbnail 
                            <span class="tw-text-xs tw-text-gray-500 tw-font-normal">(Optional)</span>
                        </label>
                        
                        <!-- Thumbnail Upload Area -->
                        <div v-if="!newThumbnail">
                            <div
                                @drop.prevent="handleThumbnailDrop"
                                @dragover.prevent="isDraggingThumbnail = true"
                                @dragleave.prevent="isDraggingThumbnail = false"
                                @click="triggerThumbnailInput"
                                :class="[
                                    'tw-relative tw-w-full tw-h-20 tw-border-2 tw-border-dashed tw-rounded-lg tw-flex tw-flex-col tw-items-center tw-justify-center tw-cursor-pointer tw-transition-all tw-duration-200',
                                    isDraggingThumbnail ? 'tw-border-primary-500 tw-bg-primary-50' : 'tw-border-gray-300 hover:tw-border-primary-400'
                                ]"
                            >
                                <icon icon="solar:gallery-add-linear" class="tw-text-xl tw-text-gray-400" />
                                <p class="tw-text-xs tw-text-gray-500 tw-text-center tw-px-2">
                                    Drop new image or click to replace<br />
                                    JPG, PNG (max 2MB)
                                </p>
                                
                                <input
                                    ref="thumbnailInput"
                                    type="file"
                                    accept=".jpg,.jpeg,.png,.gif"
                                    @change="handleThumbnailSelect"
                                    class="tw-hidden"
                                />
                            </div>
                        </div>
                        
                        <!-- New Thumbnail Preview -->
                        <div v-else class="tw-relative tw-w-full tw-max-w-sm">
                            <img
                                :src="newThumbnailPreview"
                                :alt="newThumbnail.name"
                                class="tw-w-full tw-h-32 tw-object-cover tw-rounded-lg tw-border-2 tw-border-green-500"
                            />
                            <div class="tw-absolute tw-top-2 tw-left-2 tw-bg-green-500 tw-text-white tw-text-xs tw-px-2 tw-py-1 tw-rounded">
                                New Thumbnail
                            </div>
                            <div class="tw-absolute tw-bottom-2 tw-left-2 tw-bg-black tw-bg-opacity-50 tw-text-white tw-text-xs tw-px-2 tw-py-1 tw-rounded">
                                {{ formatFileSize(newThumbnail.size) }}
                            </div>
                            <button
                                @click="removeNewThumbnail"
                                class="tw-absolute tw-top-2 tw-right-2 tw-w-6 tw-h-6 tw-bg-red-500 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center hover:tw-bg-red-600 tw-transition-colors tw-text-xs"
                            >
                                <icon icon="solar:close-linear" class="tw-text-sm" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Footer -->
            <div class="tw-p-6 tw-bg-gray-50 tw-flex tw-items-center tw-justify-end tw-gap-3">
                <a-button
                    intent="white"
                    @click="onCancel"
                    :disabled="isUpdating"
                >
                    Cancel
                </a-button>
                <a-button
                    intent="primary"
                    @click="updateDocument"
                    :disabled="isUpdating"
                    :loading="isUpdating"
                >
                    Update Document
                </a-button>
            </div>
        </div>
    </a-popup>
</template>

<script setup>
import { ref, defineProps, defineEmits, watch } from 'vue'
import FormField from '@/components/form/FormField.vue'
import { update } from '@/api/documents'
import useAlert from '@/composables/useAlert'

// Props
const props = defineProps({
    visible: {
        type: Boolean,
        default: false
    },
    document: {
        type: Object,
        default: null
    }
})

// Emits
const emit = defineEmits(['close', 'updated'])

// Reactive data
const isUpdating = ref(false)
const isDraggingFile = ref(false)
const isDraggingThumbnail = ref(false)

// File uploads
const newFile = ref(null)
const newFileType = ref('')
const fileInput = ref(null)

const newThumbnail = ref(null)
const newThumbnailPreview = ref('')
const thumbnailInput = ref(null)

// Edit form
const editForm = ref({
    name: '',
    description: '',
    status: 'active'
})

// Watch for document changes to populate form
watch(() => props.document, (newDoc) => {
    if (newDoc) {
        editForm.value = {
            name: newDoc.name || '',
            description: newDoc.description || '',
            status: newDoc.status || 'active'
        }
    }
}, { immediate: true })

// Methods
const onCancel = () => {
    resetForm()
    emit('close')
}

const resetForm = () => {
    newFile.value = null
    newFileType.value = ''
    newThumbnail.value = null
    newThumbnailPreview.value = ''
    isDraggingFile.value = false
    isDraggingThumbnail.value = false
    isUpdating.value = false
    
    if (fileInput.value) {
        fileInput.value.value = ''
    }
    if (thumbnailInput.value) {
        thumbnailInput.value.value = ''
    }
}

// File handling methods
const triggerFileInput = () => {
    fileInput.value?.click()
}

const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
        processNewFile(file)
    }
}

const handleFileDrop = (event) => {
    isDraggingFile.value = false
    const file = event.dataTransfer.files[0]
    if (file) {
        processNewFile(file)
    }
}

const processNewFile = (file) => {
    // Validate file type
    const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx']
    const fileExtension = getFileExtension(file.name)
    
    if (!allowedExtensions.includes(fileExtension)) {
        useAlert('Please select a valid file type (PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX)', 'danger')
        return
    }
    
    newFile.value = file
    newFileType.value = getFileType(file.name)
}

const removeNewFile = () => {
    newFile.value = null
    newFileType.value = ''
    if (fileInput.value) {
        fileInput.value.value = ''
    }
}

// Thumbnail handling methods
const triggerThumbnailInput = () => {
    thumbnailInput.value?.click()
}

const handleThumbnailSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
        processNewThumbnail(file)
    }
}

const handleThumbnailDrop = (event) => {
    isDraggingThumbnail.value = false
    const file = event.dataTransfer.files[0]
    if (file) {
        processNewThumbnail(file)
    }
}

const processNewThumbnail = (file) => {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
    if (!allowedTypes.includes(file.type)) {
        useAlert('Please select a valid image file (JPG, PNG, GIF)', 'danger')
        return
    }
    
    // Validate file size (2MB limit)
    const maxSize = 2 * 1024 * 1024 // 2MB
    if (file.size > maxSize) {
        useAlert('Thumbnail image must be smaller than 2MB', 'danger')
        return
    }
    
    newThumbnail.value = file
    
    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
        newThumbnailPreview.value = e.target.result
    }
    reader.readAsDataURL(file)
}

const removeNewThumbnail = () => {
    newThumbnail.value = null
    newThumbnailPreview.value = ''
    if (thumbnailInput.value) {
        thumbnailInput.value.value = ''
    }
}

// Update document
const updateDocument = async () => {
    if (!props.document) return
    
    // Validate form
    if (!editForm.value.name.trim()) {
        useAlert('Please enter a document name', 'danger')
        return
    }
    
    if (editForm.value.name.length > 255) {
        useAlert('Document name cannot exceed 255 characters', 'danger')
        return
    }
    
    if (!editForm.value.description.trim()) {
        useAlert('Please enter a document description', 'danger')
        return
    }
    
    if (editForm.value.description.length > 255) {
        useAlert('Document description cannot exceed 255 characters', 'danger')
        return
    }
    
    isUpdating.value = true
    
    try {
        // Create FormData for the update
        const formData = new FormData()
        formData.append('name', editForm.value.name.trim())
        formData.append('description', editForm.value.description.trim())
        formData.append('status', editForm.value.status)
        
        // Add update flags
        formData.append('is_file_updated', newFile.value ? 'true' : 'false')
        formData.append('is_thumbnail_updated', newThumbnail.value ? 'true' : 'false')
        
        // Add new file if provided
        if (newFile.value) {
            formData.append('file', newFile.value)
            formData.append('type', newFileType.value)
            formData.append('size', newFile.value.size.toString())
            formData.append('extension', getFileExtension(newFile.value.name))
            formData.append('mime_type', newFile.value.type || 'application/octet-stream')
        }
        
        // Add new thumbnail if provided
        if (newThumbnail.value) {
            formData.append('thumbnail', newThumbnail.value)
        }
        
        // Call the API
        const response = await update(props.document.id, formData)
        
        useAlert('Document updated successfully', 'success')
        emit('updated', response.data)
        resetForm()
        emit('close')
        
    } catch (error) {
        console.error('Error updating document:', error)
        useAlert('Failed to update document', 'danger')
    } finally {
        isUpdating.value = false
    }
}

// Utility functions
const getFileExtension = (fileName) => {
    return fileName.split('.').pop().toLowerCase()
}

const getFileType = (fileName) => {
    const extension = getFileExtension(fileName)
    const typeMap = {
        pdf: 'pdf',
        doc: 'doc',
        docx: 'doc',
        xls: 'spreadsheet',
        xlsx: 'spreadsheet',
        ppt: 'presentation',
        pptx: 'presentation'
    }
    return typeMap[extension] || 'document'
}

const getFileIcon = (type) => {
    const iconMap = {
        pdf: 'solar:document-text-bold-duotone',
        doc: 'solar:document-bold-duotone',
        spreadsheet: 'solar:chart-square-bold-duotone',
        presentation: 'solar:presentation-graph-bold-duotone',
        document: 'solar:file-bold-duotone'
    }
    return iconMap[type] || iconMap.document
}

const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes'
    
    const size = typeof bytes === 'string' ? parseInt(bytes) : bytes
    if (isNaN(size)) return '0 Bytes'
    
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(size) / Math.log(k))
    return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    })
}
</script> 